import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Lock, Mail, Images as Albums, Camera, Calendar } from "lucide-react";
import { resolveFileUrl } from "../lib/urls";
import Logo from "../components/Logo";

/**
 * SuperAlbumPublicPage — the shareable `/album/:slug` view.
 *
 * Flow:
 *  1. Fetch metadata (title/cover/event list) — always available.
 *  2. If server reports `access_granted=false`, show a passcode + email
 *     unlock card.
 *  3. On successful unlock, server sets an httpOnly `sa_<id>` cookie and
 *     we re-fetch the full payload (now with `grouped` photos).
 */
const SuperAlbumPublicPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [passcode, setPasscode] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [unlockErr, setUnlockErr] = useState("");
  const [lightbox, setLightbox] = useState(null);

  const base =
    (typeof window !== "undefined" && window.location && window.location.origin) ||
    process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try {
      const r = await axios.get(`${base}/api/public/super-albums/${slug}`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Super Album not found");
    }
  };
  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const unlock = async (e) => {
    e.preventDefault();
    setBusy(true); setUnlockErr("");
    try {
      await axios.post(
        `${base}/api/public/super-albums/${slug}/unlock`,
        { passcode: passcode || null, email: email || null },
        { withCredentials: true, headers: { "X-Requested-With": "XMLHttpRequest" } },
      );
      await load();
    } catch (e2) {
      setUnlockErr(e2?.response?.data?.detail || "Unlock failed. Try again.");
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="min-h-[80vh] bg-cream-100 flex items-center justify-center text-navy-700/60" data-testid="super-album-not-found">
        {err}
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-[80vh] flex items-center justify-center text-navy-700/50">Loading album…</div>;
  }

  // Unlock gate
  if (!data.access_granted) {
    return (
      <div className="min-h-screen bg-cream-100 py-16 px-5" data-testid="super-album-unlock-screen">
        <div className="max-w-lg mx-auto">
          <div className="flex justify-center mb-6"><Logo size="sm" /></div>
          <div className="bg-white rounded-3xl soft-shadow overflow-hidden">
            {data.cover_image ? (
              <img src={data.cover_image} alt="" className="w-full h-48 object-cover" />
            ) : (
              <div className="h-32 bg-gradient-to-br from-coral-200 to-cream-200" />
            )}
            <div className="p-8">
              <div className="flex items-center gap-2 text-coral-500 text-xs uppercase tracking-widest font-semibold mb-2">
                <Albums className="w-4 h-4" /> Super Album
              </div>
              <h1 className="font-serif italic text-navy-700 text-3xl">{data.title}</h1>
              {data.description && <p className="text-navy-700/70 text-sm mt-2">{data.description}</p>}
              <p className="text-navy-700/60 text-sm mt-3">
                {data.events?.length || 0} events bundled together.
              </p>

              <form onSubmit={unlock} className="mt-6 space-y-3">
                {data.has_passcode && (
                  <div>
                    <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider inline-flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5" /> Passcode
                    </label>
                    <input
                      value={passcode}
                      onChange={e => setPasscode(e.target.value)}
                      maxLength={12}
                      autoComplete="off"
                      placeholder="Enter the passcode"
                      data-testid="super-album-passcode"
                      className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300"
                    />
                  </div>
                )}
                <div className="relative">
                  <div className="flex items-center gap-2 my-3">
                    <div className="flex-1 h-px bg-cream-200" />
                    <span className="text-xs text-navy-700/40 uppercase tracking-wider">or</span>
                    <div className="flex-1 h-px bg-cream-200" />
                  </div>
                  <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider inline-flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" /> Email on the guest list
                  </label>
                  <input
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    type="email"
                    placeholder="your-email@example.com"
                    data-testid="super-album-email"
                    className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300"
                  />
                </div>
                {unlockErr && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="super-album-unlock-err">{unlockErr}</div>}
                <button
                  type="submit"
                  disabled={busy || (!passcode && !email)}
                  data-testid="super-album-unlock-btn"
                  className="w-full bg-coral-300 text-white py-3 rounded-full font-semibold hover:bg-coral-400 disabled:opacity-50"
                >
                  {busy ? "Unlocking…" : "Open album"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Access granted — unified gallery, grouped by event.
  return (
    <div className="min-h-screen bg-cream-100" data-testid="super-album-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy-700 text-white">
        <div className="absolute top-4 left-5 z-10">
          <Logo size="sm" tint="white" className="drop-shadow-lg" />
        </div>
        {data.cover_image && (
          <img src={data.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        )}
        <div className="relative max-w-5xl mx-auto px-6 py-20 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold mb-5">
            <Albums className="w-3.5 h-3.5" /> Super Album
          </div>
          <h1 className="font-serif italic text-4xl md:text-6xl mb-3 leading-tight" data-testid="super-album-title">{data.title}</h1>
          {data.description && <p className="text-white/80 text-sm max-w-xl mx-auto">{data.description}</p>}
          <div className="mt-5 text-white/70 text-xs" data-testid="super-album-counts">
            {data.event_count} events · {data.total_uploads} photos & videos
          </div>
        </div>
      </section>

      {/* Grouped gallery */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-12 space-y-14">
        {data.grouped?.map((group, gi) => (
          <div key={group.id} data-testid={`super-album-group-${gi}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-serif italic text-navy-700 text-3xl">{group.name}</h2>
                  {group.expired && (
                    <span
                      data-testid={`super-album-group-${gi}-expired`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-navy-700/10 text-navy-700 px-2 py-1 rounded-full"
                    >
                      Archived
                    </span>
                  )}
                </div>
                {group.date && (
                  <div className="text-sm text-navy-700/60 inline-flex items-center gap-1.5 mt-1">
                    <Calendar className="w-3.5 h-3.5" /> {group.date}
                  </div>
                )}
              </div>
              {!group.expired && (
                <a
                  href={`/g/${group.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-coral-500 hover:text-coral-600 font-semibold inline-flex items-center gap-1"
                >
                  Open the full event gallery →
                </a>
              )}
            </div>
            {group.uploads.length === 0 ? (
              <div className="bg-white rounded-2xl p-10 text-center text-navy-700/50 text-sm">
                <Camera className="w-6 h-6 mx-auto mb-2 opacity-40" />
                No photos yet for this event.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {group.uploads.map(u => (
                  <button
                    key={u.id}
                    onClick={() => setLightbox(u)}
                    className="relative aspect-square rounded-xl overflow-hidden bg-cream-200 group block w-full"
                    data-testid={`super-album-upload-${u.id}`}
                  >
                    {u.kind === "photo" ? (
                      <img
                        src={resolveFileUrl(u.file_url)}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <>
                        <video src={resolveFileUrl(u.file_url)} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center">
                            <Camera className="w-5 h-5 text-coral-300" />
                          </div>
                        </div>
                      </>
                    )}
                    {u.uploader_name && (
                      <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/60 to-transparent text-white text-[11px] font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                        {u.uploader_name}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </section>

      <footer className="text-center py-10 text-navy-700/50 text-xs">
        Powered by <span className="font-serif italic text-navy-700">GalerieCop</span>
      </footer>

      {lightbox && (
        <div onClick={() => setLightbox(null)} className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          {lightbox.kind === "photo" ? (
            <img src={resolveFileUrl(lightbox.file_url)} alt="" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
          ) : (
            <video src={resolveFileUrl(lightbox.file_url)} controls autoPlay className="max-w-full max-h-[90vh] rounded-lg" />
          )}
        </div>
      )}
    </div>
  );
};

export default SuperAlbumPublicPage;
